// src/pages/team/TeamOwnershipPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { applicationStatusMap, getStatusMapping, getEntityId, formatDateTime, getDisplayName, refEntity } from '@/features/team/logic';
import { EntityAvatar } from '@/components/EntityAvatar';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';
import { Select } from '@/components/Select';

export const TeamOwnershipPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { t, locale } = useI18n();

  const [transfers, setTransfers] = useState<any[]>([]);
  const [showTransfer, setShowTransfer] = useState(false);
  const [targetProfileId, setTargetProfileId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmTransfer, setConfirmTransfer] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Multi-owner management (GrantTeamOwnership / RevokeTeamOwnership)
  const [owners, setOwners] = useState<any[]>([]);
  const [otherMembers, setOtherMembers] = useState<any[]>([]);
  const [grantTargetId, setGrantTargetId] = useState('');
  const [granting, setGranting] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Formal transfer request (CreateTeamOwnershipTransfer)
  const [showPropose, setShowPropose] = useState(false);
  const [proposeTargetId, setProposeTargetId] = useState('');
  const [proposeMessage, setProposeMessage] = useState('');
  const [proposing, setProposing] = useState(false);
  const [viewingTransfer, setViewingTransfer] = useState<any | null>(null);
  const [viewTransferLoading, setViewTransferLoading] = useState(false);

  const loadTransfers = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListTeamOwnershipTransfers(id);
      setTransfers(res.transfers?.transfers ?? []);
    } catch { /* store handles */ }
  }, [id, store]);

  const loadOwners = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListTeamMembers(id);
      const list = (res as any)?.users?.users ?? [];
      setOwners(list.filter((m: any) => m.is_owner));
      setOtherMembers(list.filter((m: any) => !m.is_owner));
    } catch { /* store handles */ }
  }, [id, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadTransfers();
    loadOwners();
  }, [id, loadTransfers, loadOwners]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleTransfer = async () => {
    if (!id || !targetProfileId.trim()) return;
    if (!confirmTransfer) {
      setConfirmTransfer(true);
      return;
    }
    setSubmitting(true);
    try {
      await store.TransferTeamOwnership(id, {
        team: refEntity(id),
        new_owner: refEntity(targetProfileId.trim()),
      });
      setTargetProfileId('');
      setShowTransfer(false);
      setConfirmTransfer(false);
      showSuccess(t('team.transferInitiated'));
      loadTransfers();
    } catch { /* store handles */ } finally { setSubmitting(false); }
  };

  const handleCancelTransfer = async (transferId: string) => {
    if (!id) return;
    try {
      await store.CancelTeamOwnershipTransfer(transferId, { transfer: refEntity(transferId), comment: '' });
      showSuccess(t('team.transferCancelled'));
      loadTransfers();
    } catch { /* store handles */ }
  };

  const handleGrantOwnership = async () => {
    if (!id || !grantTargetId) return;
    setGranting(true);
    try {
      await store.GrantTeamOwnership(id, { team: { id }, user: { id: grantTargetId } } as never);
      setGrantTargetId('');
      showSuccess(t('team.ownershipGranted'));
      loadOwners();
    } catch { /* store handles */ } finally { setGranting(false); }
  };

  const handleRevokeOwnership = async (userId: string) => {
    if (!id) return;
    try {
      await store.RevokeTeamOwnership(id, { team: { id }, user: { id: userId } } as never);
      showSuccess(t('team.ownershipRevoked'));
      loadOwners();
    } catch { /* store handles */ } finally { setRevokingId(null); }
  };

  const handleProposeTransfer = async () => {
    if (!id || !proposeTargetId.trim()) return;
    setProposing(true);
    try {
      await store.CreateTeamOwnershipTransfer(id, {
        transfer: {
          team: { id },
          message: proposeMessage.trim(),
          to_owner: { id: proposeTargetId.trim() } as never,
        } as never,
      });
      setProposeTargetId('');
      setProposeMessage('');
      setShowPropose(false);
      showSuccess(t('team.transferProposed'));
      loadTransfers();
    } catch { /* store handles */ } finally { setProposing(false); }
  };

  const handleViewTransfer = async (tr: any) => {
    const trId = getEntityId(tr);
    if (!trId) return;
    setViewTransferLoading(true);
    try {
      const res = await store.GetTeamOwnershipTransfer(trId);
      setViewingTransfer(res?.transfer ?? tr);
    } catch {
      setViewingTransfer(tr);
    } finally {
      setViewTransferLoading(false);
    }
  };

  const teamName = (store.currentItem as any)?.name || t('team.title');
  const currentOwner = (store.currentItem as any)?.owner;

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('team.nav.ownership')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="people" active="ownership" />


      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('team.ownershipTitle')}</h1>

      {store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      {/* Current Owner */}
      <Card>
        <CardHeader>
          <CardTitle>{t('team.currentOwner')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <EntityAvatar name={getDisplayName(currentOwner)} image={currentOwner?.avatar} profileId={currentOwner?.entity?.id} className="h-12 w-12 text-lg" />
            <div>
              <p className="text-sm font-medium text-foreground">{getDisplayName(currentOwner)}</p>
              <p className="text-xs text-muted-foreground">{currentOwner?.id || ''}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Owners (multi-owner) */}
      <Card>
        <CardHeader>
          <CardTitle>{t('team.owners')}</CardTitle>
          <CardDescription>{t('team.ownersDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {owners.length > 0 && (
            <div className="space-y-2">
              {owners.map((o: any) => {
                const ownerUserId = o.user?.entity?.id || '';
                const name = o.user?.display_name || ownerUserId;
                return (
                  <div key={ownerUserId || Math.random()} className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <EntityAvatar name={name} image={o.user?.avatar} profileId={o.profile?.id} size="sm" />
                      <span className="text-sm font-medium text-foreground">{name}</span>
                      <Badge variant="warning" size="sm">{t('team.field.owner')}</Badge>
                    </div>
                    {revokingId === ownerUserId ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-destructive whitespace-nowrap">{t('team.revokeOwnershipConfirm')}</span>
                        <Button size="sm" variant="danger" onClick={() => handleRevokeOwnership(ownerUserId)}>{t('common.confirm')}</Button>
                        <Button size="sm" variant="ghost" onClick={() => setRevokingId(null)}>{t('common.cancel')}</Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        disabled={owners.length <= 1}
                        title={owners.length <= 1 ? t('team.lastOwnerWarning') : undefined}
                        onClick={() => setRevokingId(ownerUserId)}
                      >
                        {t('team.revokeOwnership')}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {otherMembers.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-border">
              <Select value={grantTargetId} onChange={(e) => setGrantTargetId(e.target.value)} className="flex-1">
                <option value="">{t('team.selectMemberToGrant')}</option>
                {otherMembers.map((m: any) => (
                  <option key={m.user?.entity?.id} value={m.user?.entity?.id}>{m.user?.display_name || m.user?.entity?.id}</option>
                ))}
              </Select>
              <Button onClick={handleGrantOwnership} disabled={!grantTargetId || granting}>
                {granting ? t('common.loading') : t('team.grantOwnership')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Propose a formal transfer request */}
      <Card>
        <CardHeader>
          <CardTitle>{t('team.proposeTransfer')}</CardTitle>
          <CardDescription>{t('team.proposeTransferDescription')}</CardDescription>
        </CardHeader>
        {showPropose ? (
          <>
            <CardContent className="space-y-4">
              <Input
                label={t('team.newOwnerProfileId')}
                type="text"
                value={proposeTargetId}
                onChange={(e) => setProposeTargetId(e.target.value)}
                placeholder={t('team.newOwnerPlaceholder')}
                required
              />
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-foreground">{t('team.transferReason')}</label>
                <textarea
                  value={proposeMessage}
                  onChange={(e) => setProposeMessage(e.target.value)}
                  placeholder={t('team.transferReasonPlaceholder')}
                  rows={2}
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowPropose(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleProposeTransfer} disabled={proposing || !proposeTargetId.trim()}>
                {proposing ? t('common.loading') : t('team.proposeTransfer')}
              </Button>
            </CardFooter>
          </>
        ) : (
          <CardFooter>
            <Button variant="secondary" onClick={() => setShowPropose(true)}>{t('team.proposeTransfer')}</Button>
          </CardFooter>
        )}
      </Card>

      {/* Transfer Ownership */}
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="text-destructive">{t('team.transferOwnership')}</CardTitle>
          <CardDescription>{t('team.transferOwnershipDescription')}</CardDescription>
        </CardHeader>
        {showTransfer ? (
          <>
            <CardContent className="space-y-4">
              <Input
                label={t('team.newOwnerProfileId')}
                type="text"
                value={targetProfileId}
                onChange={(e) => { setTargetProfileId(e.target.value); setConfirmTransfer(false); }}
                placeholder={t('team.newOwnerPlaceholder')}
                required
              />
              {confirmTransfer && (
                <Alert variant="warning">
                  {t('team.transferConfirmWarning')}
                </Alert>
              )}
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setShowTransfer(false); setConfirmTransfer(false); }}>{t('common.cancel')}</Button>
              <Button variant="danger" onClick={handleTransfer} disabled={submitting || !targetProfileId.trim()}>
                {confirmTransfer ? t('team.confirmTransfer') : t('team.transferOwnership')}
              </Button>
            </CardFooter>
          </>
        ) : (
          <CardFooter>
            <Button variant="danger" onClick={() => setShowTransfer(true)}>{t('team.initiateTransfer')}</Button>
          </CardFooter>
        )}
      </Card>

      {/* Transfer History */}
      <Card>
        <CardHeader>
          <CardTitle>{t('team.transferHistory')}</CardTitle>
        </CardHeader>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.from')}</TableHead>
              <TableHead>{t('common.to')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('common.createdAt')}</TableHead>
              <TableHead>{t('common.completed')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transfers.length === 0 ? (
              <TableEmpty colSpan={6} message={t('team.noTransfers')} />
            ) : (
              transfers.map((tr: any) => {
                const trId = getEntityId(tr);
                const isPending = tr.status === 'APPLICATION_STATUS_PENDING';
                const sm = getStatusMapping(applicationStatusMap, tr.status);
                return (
                  <TableRow key={trId || Math.random()}>
                    <TableCell><span className="text-sm">{getDisplayName(tr.from_owner)}</span></TableCell>
                    <TableCell><span className="text-sm">{getDisplayName(tr.to_owner)}</span></TableCell>
                    <TableCell>
                      <Badge variant={sm.variant} size="sm">{t(sm.labelKey)}</Badge>
                    </TableCell>
                    <TableCell><span className="text-sm text-muted-foreground">{formatDateTime(tr.created_at, locale)}</span></TableCell>
                    <TableCell><span className="text-sm text-muted-foreground">{formatDateTime(tr.completed_at, locale)}</span></TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleViewTransfer(tr)}>
                          {t('common.view')}
                        </Button>
                        {isPending && (
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleCancelTransfer(trId)}>
                            {t('common.cancel')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {viewingTransfer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="button"
          tabIndex={0}
          onClick={() => setViewingTransfer(null)}
          onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') setViewingTransfer(null); }}
        >
          <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" />
          <Card className="relative w-full max-w-md" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>{t('team.transferDetailTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {viewTransferLoading ? (
                <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
              ) : (
                <dl className="divide-y divide-border">
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.from')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{getDisplayName(viewingTransfer.from_owner)}</dd>
                  </div>
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.to')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{getDisplayName(viewingTransfer.to_owner)}</dd>
                  </div>
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.status')}</dt>
                    <dd className="text-sm text-foreground col-span-2">
                      <Badge variant={getStatusMapping(applicationStatusMap, viewingTransfer.status).variant} size="sm">
                        {t(getStatusMapping(applicationStatusMap, viewingTransfer.status).labelKey)}
                      </Badge>
                    </dd>
                  </div>
                  {viewingTransfer.message && (
                    <div className="py-2 grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-muted-foreground">{t('common.message')}</dt>
                      <dd className="text-sm text-foreground col-span-2">{viewingTransfer.message}</dd>
                    </div>
                  )}
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.createdAt')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{formatDateTime(viewingTransfer.created_at, locale)}</dd>
                  </div>
                </dl>
              )}
              <div className="flex justify-end pt-2">
                <Button variant="secondary" onClick={() => setViewingTransfer(null)}>{t('common.close')}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
});

export default TeamOwnershipPage;
